import type { ReactElement } from "react";
import type { Layout } from "@kundeninfo/api-client";
import { Register, Reiter } from "@kundeninfo/ui/me-design";
import type { Entwurf } from "./layoutEntwurf.js";

/**
 * Die REGISTER der Gestaltung (Spec, Oberfläche, rechts): Marke, Seite, Sektionen,
 * Tabelle, Blöcke, Texte. Welche es gibt, sagt der Editor über `eintraege` — so wächst
 * die Liste mit K9/K10, ohne dass dieses Gerüst sich ändert.
 */

export type RegisterId = "marke" | "seite" | "sektionen" | "tabelle" | "bloecke" | "texte";

export type PanelProps = {
  stand: Layout;
  entwurf: Entwurf;
  feldFehler: Record<string, string>;
  belegart: string;
  onEntwurf: (neu: Entwurf) => void;
  onFeldVerlassen: () => void;
};

export type RegisterEintrag = {
  id: RegisterId; label: string; panel: (p: PanelProps) => ReactElement;
};

/** Id des Panels zu einem Register — zugleich die Kennung seines `me-reiter` (`fuer`):
 *  Das Register meldet sie in `me-reiter-gewaehlt` und erwartet sie in `aktiv`. */
function panelId(id: RegisterId): string {
  return `lay-register-${id}`;
}

/**
 * Die Reiterleiste seit ME-Etappe 3 (Aufgabe 6): `me-register` mit einem `me-reiter` je
 * Register (Hausregel „Reiter nach unten offen" — die Mechanik bringt das Element mit) statt
 * der eigenen `.lay__reiter`-Leiste. `role="tablist"`/`role="tab"` stehen im Shadow DOM;
 * `aria-label` am Host spiegelt me-register auf seine tablist. `fuer` setzt dort
 * `aria-controls`, das über die Schattengrenze nicht auf das Panel zeigt — das Panel trägt
 * seinen Namen deshalb selbst (`aria-label`, Muster KundenAkte.tsx).
 *
 * Kein Fehlerzähler (`Reiter fehler`): Der Editor kennt seine Feldbefunde je Pfad, nicht
 * je Register — die Zuordnung gehört zum Arbeitsdialog-Paket (Plan 2026-10-09).
 */
export function LayoutRegister(p: PanelProps & {
  eintraege: readonly RegisterEintrag[]; aktiv: RegisterId; onAktiv: (id: RegisterId) => void;
}): ReactElement {
  const aktiv = p.eintraege.find((e) => e.id === p.aktiv) ?? p.eintraege[0];
  return (
    <div className="lay__register">
      <Register className="lay__register-leiste" aria-label="Gestaltung"
        {...(aktiv === undefined ? {} : { aktiv: panelId(aktiv.id) })}
        onReiterGewaehlt={(d) => {
          const gewaehlt = p.eintraege.find((e) => panelId(e.id) === d.id);
          if (gewaehlt !== undefined) p.onAktiv(gewaehlt.id);
        }}>
        {p.eintraege.map((e) => (
          <Reiter key={e.id} fuer={panelId(e.id)} beschriftung={e.label} />
        ))}
      </Register>
      {aktiv !== undefined && (
        <div id={panelId(aktiv.id)} role="tabpanel" aria-label={aktiv.label}
          className="lay__registerinhalt">
          {aktiv.panel(p)}
        </div>
      )}
    </div>
  );
}
